import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Compass, Search } from "lucide-react";
import { api } from "@/lib/api";
import { ConstellationShareButton } from "@/components/ConstellationShare";

const CATEGORY_COLORS = {
  Physique: "#7fb3ff", Chimie: "#8affd1", Biologie: "#a0f0a0", Mathématiques: "#f0c674",
  Histoire: "#e0a878", Philosophie: "#c9a0ff", Art: "#ff9ecd", Littérature: "#ffd29e",
  Astronomie: "#9ecbff", Géographie: "#7fe0c0", Musique: "#e0a0ff", Technologie: "#a0c0ff",
  Langues: "#ffb0b0", Économie: "#c0e080", Logique: "#ff9d5c", Autre: "#e6d3a3",
};
const colorFor = (c) => CATEGORY_COLORS[c] || CATEGORY_COLORS.Autre;

export default function Constellation({ onExplore }) {
  const { t } = useTranslation();
  const [data, setData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => {
    api.get("/constellation")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, []);

  const allGroups = useMemo(() => {
    const byCat = {};
    (data.nodes || []).forEach((n) => {
      const cat = (n.category || "Autre").trim() || "Autre";
      (byCat[cat] = byCat[cat] || []).push(n);
    });
    return Object.entries(byCat)
      .map(([category, subjects]) => ({
        category,
        subjects: subjects.slice().sort((a, b) => (b.count || 1) - (a.count || 1)),
      }))
      .sort((a, b) => b.subjects.length - a.subjects.length || a.category.localeCompare(b.category));
  }, [data.nodes]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allGroups
      .filter((g) => activeCat === "all" || g.category === activeCat)
      .map((g) => ({
        ...g,
        subjects: q ? g.subjects.filter((s) => (s.name || "").toLowerCase().includes(q)) : g.subjects,
      }))
      .filter((g) => g.subjects.length > 0);
  }, [allGroups, query, activeCat]);

  const relaunch = (name) => onExplore?.(t("atelier.relaunchPrompt", { name }));

  if (loading) {
    return (
      <div className="h-full starfield flex items-center justify-center" data-testid="constellation-list">
        <p className="font-display italic text-foreground/40 text-xl">{t("atelier.mapping")}</p>
      </div>
    );
  }

  if (!data.nodes || data.nodes.length === 0) {
    return (
      <div className="h-full starfield flex items-center justify-center px-6 text-center" data-testid="constellation-list">
        <p className="font-display italic text-foreground/50 text-2xl max-w-md">{t("atelier.emptySky")}</p>
      </div>
    );
  }

  return (
    <div className="h-full starfield overflow-y-auto" data-testid="constellation-list">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <header className="mb-8 text-center">
          <h2 className="font-display text-gradient-orange text-3xl sm:text-4xl italic" data-testid="constellation-title">
            {t("atelier.myConstellation")}
          </h2>
          <p className="text-foreground/50 text-sm mt-2">
            {t("atelier.constellationStats", { stars: data.nodes.length, links: data.links.length })}
          </p>
          <div className="mt-5 flex justify-center">
            <ConstellationShareButton nodes={data.nodes} links={data.links} />
          </div>
        </header>

        <div className="mb-8 space-y-4 sticky top-0 z-10 py-2 -mx-1 px-1 bg-background/70 backdrop-blur-md">
          <div className="relative max-w-md mx-auto">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/40" />
            <input
              data-testid="constellation-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("atelier.searchPlaceholder")}
              className="w-full rounded-full bg-white/[0.03] border border-white/12 pl-11 pr-4 py-2.5 text-sm text-foreground placeholder:text-foreground/30 focus:outline-none focus:border-primary/45 transition-colors"
            />
          </div>
          <div className="flex flex-wrap justify-center gap-2" data-testid="constellation-filters">
            <button
              data-testid="filter-all"
              onClick={() => setActiveCat("all")}
              className={`px-3.5 py-1.5 rounded-full text-xs transition-colors ${activeCat === "all" ? "bg-primary/20 text-primary" : "text-foreground/50 hover:text-foreground/80 glass"}`}
            >
              {t("atelier.allDomains")}
            </button>
            {allGroups.map((g) => {
              const c = colorFor(g.category);
              const on = activeCat === g.category;
              return (
                <button
                  key={g.category}
                  data-testid={`filter-${g.category}`}
                  onClick={() => setActiveCat(on ? "all" : g.category)}
                  className={`px-3.5 py-1.5 rounded-full text-xs transition-colors inline-flex items-center gap-1.5 ${on ? "" : "glass text-foreground/50 hover:text-foreground/80"}`}
                  style={on ? { background: c + "26", color: c } : undefined}
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: c }} />
                  {g.category}
                </button>
              );
            })}
          </div>
        </div>

        {groups.length === 0 ? (
          <p className="font-display italic text-foreground/40 text-lg py-10" data-testid="constellation-no-results">
            {t("atelier.noResults")}
          </p>
        ) : (
        <div className="space-y-12">
          {groups.map((group) => (
            <section key={group.category} data-testid={`constellation-group-${group.category}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: colorFor(group.category), boxShadow: `0 0 12px ${colorFor(group.category)}` }} />
                <h3 className="uppercase tracking-[0.22em] text-xs font-semibold" style={{ color: colorFor(group.category) }}>
                  {group.category}
                </h3>
                <span className="text-foreground/30 text-xs">· {group.subjects.length}</span>
                <span className="flex-1 h-px bg-foreground/10 ml-2" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {group.subjects.map((s, i) => {
                  const c = colorFor(s.category);
                  const count = s.count || 1;
                  return (
                    <motion.div
                      key={s.id || s.name}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4) }}
                      data-testid={`subject-card-${s.name}`}
                      className="group glass frame-pulse rounded-2xl p-5 flex flex-col gap-3"
                      style={{ borderLeft: `3px solid ${c}` }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-display text-xl text-foreground leading-tight truncate">{s.name}</p>
                          <p className="uppercase tracking-[0.18em] text-[10px] mt-1.5" style={{ color: c }}>
                            {s.category}
                          </p>
                        </div>
                        <span className="text-foreground/40 text-xs whitespace-nowrap mt-1">
                          {count > 1 ? t("atelier.exploredTimes", { count }) : t("atelier.exploredOnce")}
                        </span>
                      </div>
                      <button
                        data-testid={`subject-relaunch-${s.name}`}
                        onClick={() => relaunch(s.name)}
                        className="self-start inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-primary transition-colors"
                      >
                        <Compass size={15} /> {t("atelier.relaunch")}
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
        )}
      </div>
    </div>
  );
}
